import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  Building2,
  AlertTriangle,
  CheckCircle2,
  Circle,
  ChevronRight,
  Sparkles,
  Gavel,
  ShieldAlert,
  ListChecks,
  Wrench,
  Activity,
} from "lucide-react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";

const HEALTH_STYLES = {
  green: { dot: "bg-[#A3C293] shadow-[0_0_10px_rgba(163,194,147,0.7)]", text: "text-[#A3C293]", border: "border-[#4D5B46]/40" },
  yellow: { dot: "bg-champagne shadow-[0_0_10px_rgba(212,175,55,0.7)]", text: "text-champagne", border: "border-champagne/40" },
  red: { dot: "bg-[#E5A09E] shadow-[0_0_10px_rgba(229,160,158,0.7)]", text: "text-[#E5A09E]", border: "border-[#5C2B29]/50" },
};

const HealthDot = ({ health }) => {
  const s = HEALTH_STYLES[health] || HEALTH_STYLES.yellow;
  return <span className={`w-2 h-2 rounded-full ${s.dot}`} />;
};

const Kpi = ({ label, value, icon: Icon, tone = "muted", testId }) => (
  <div
    data-testid={testId}
    className={`regal-card p-5 border ${
      tone === "gold" ? "border-champagne/30" : "border-seam"
    }`}
  >
    <div className="flex items-center justify-between">
      <span className="label-eyebrow">{label}</span>
      <Icon className="w-4 h-4 text-champagne" strokeWidth={1.5} />
    </div>
    <p className="font-sans text-3xl font-light mt-2">{value}</p>
  </div>
);

const CommandCenter = () => {
  const { admin, bootstrapAvailable, refreshAdmin, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bootstrapping, setBootstrapping] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/enterprise/command-center");
      setData(data);
    } catch (err) {
      if (err.response?.status !== 403) {
        toast.error(err.response?.data?.detail || "Could not load command center");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (admin) load();
    else setLoading(false);
  }, [admin, load]);

  const claim = async () => {
    setBootstrapping(true);
    try {
      await api.post("/admin/bootstrap");
      toast.success("Owner / CEO authority claimed.");
      await refreshAdmin();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bootstrap failed");
    } finally {
      setBootstrapping(false);
    }
  };

  if (!admin && bootstrapAvailable) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto regal-card p-10 mt-8 text-center tracing-beam">
          <p className="label-eyebrow">R.S.V.P. Enterprise OS</p>
          <h1 className="font-heading text-4xl mt-3 mb-3">Claim Owner / CEO authority</h1>
          <p className="text-textmuted leading-relaxed mb-6">
            No administrator exists yet. As the first authenticated user you may claim
            Owner / CEO Super Admin. Future admins must be invited from the Admin Access Center.
          </p>
          <button
            onClick={claim}
            disabled={bootstrapping}
            data-testid="bootstrap-btn"
            className="inline-flex items-center gap-2 bg-champagne text-obsidian hover:bg-champagne-soft font-medium rounded-md px-6 py-3 transition-all shadow-[0_0_25px_rgba(212,175,55,0.2)] disabled:opacity-60"
          >
            {bootstrapping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gavel className="w-4 h-4" strokeWidth={1.5} />}
            Claim Owner / CEO Super Admin
          </button>
          <p className="text-xs text-textmuted mt-4">Signed in as {user?.email}</p>
        </div>
      </Layout>
    );
  }

  if (!admin) {
    return (
      <Layout>
        <div className="max-w-xl mx-auto regal-card p-10 mt-8 text-center">
          <h1 className="font-heading text-3xl mb-3">Access limited</h1>
          <p className="text-textmuted">
            You do not currently have R.S.V.P. Enterprise admin access. Ask an Owner / CEO
            to invite you from the Admin Access Center.
          </p>
        </div>
      </Layout>
    );
  }

  if (loading || !data) {
    return (
      <Layout>
        <div className="flex justify-center py-32">
          <Loader2 className="w-6 h-6 text-champagne animate-spin" />
        </div>
      </Layout>
    );
  }

  const c = data.counts;

  return (
    <Layout>
      <section className="mb-8">
        <p className="label-eyebrow">Executive</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">
          CEO Command Center
        </h1>
        <p className="text-textmuted mt-3 max-w-2xl leading-relaxed">
          The authoritative overview of R.S.V.P. — every company, every open matter, every
          decision awaiting your hand.
        </p>
      </section>

      <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-8">
        <Kpi testId="kpi-companies" label="Companies" value={c.companies} icon={Building2} tone="gold" />
        <Kpi testId="kpi-approvals" label="Pending Approvals" value={c.pending_approvals} icon={Gavel} tone={c.pending_approvals > 0 ? "gold" : "muted"} />
        <Kpi testId="kpi-overdue" label="Overdue Tasks" value={c.overdue_tasks} icon={AlertTriangle} tone={c.overdue_tasks > 0 ? "gold" : "muted"} />
        <Kpi testId="kpi-issues" label="Open Issues" value={c.open_issues} icon={Wrench} />
        <Kpi testId="kpi-risks" label="Open Risks" value={c.open_risks} icon={ShieldAlert} tone={c.critical_risks > 0 ? "gold" : "muted"} />
        <Kpi testId="kpi-projects" label="Active Projects" value={c.active_projects} icon={ListChecks} />
        <Kpi testId="kpi-kpis" label="KPIs Tracked" value={c.kpis_tracked} icon={Activity} />
        <Kpi testId="kpi-vaelithon" label="Vaelithon Pending" value={c.vaelithon_pending ?? c.nexaura_pending} icon={Sparkles} />
        <Kpi testId="kpi-entities" label="Entities Total" value={c.entities_total} icon={Building2} />
        <Kpi testId="kpi-critical" label="Critical Risks" value={c.critical_risks} icon={AlertTriangle} tone={c.critical_risks > 0 ? "gold" : "muted"} />
      </section>

      {/* Company health cards */}
      <section className="mb-10">
        <h2 className="font-heading text-2xl mb-4">Companies & Health</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.companies.map((co) => {
            const s = HEALTH_STYLES[co.health] || HEALTH_STYLES.yellow;
            return (
              <button
                key={co.id}
                type="button"
                onClick={() => navigate(`/companies/${co.id}`)}
                data-testid={`co-card-${co.id}`}
                className={`text-left regal-card p-5 border ${s.border} hover:border-champagne/50 transition-all group`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <HealthDot health={co.health} />
                      <h3 className="font-heading text-xl text-textmain truncate">{co.name}</h3>
                    </div>
                    <p className="text-xs text-textmuted mt-1">
                      {co.entity_type} · {co.status}
                      {co.abbreviation ? ` · ${co.abbreviation}` : ""}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-textmuted group-hover:text-champagne" strokeWidth={1.5} />
                </div>
                <div className="mt-4 grid grid-cols-4 gap-2 text-xs text-textmuted">
                  <div><span className="text-textmain font-medium">{co.open_tasks}</span> tasks</div>
                  <div><span className="text-textmain font-medium">{co.pending_approvals}</span> approvals</div>
                  <div><span className="text-textmain font-medium">{co.open_issues}</span> issues</div>
                  <div><span className={s.text}>{co.critical_risks}</span> critical</div>
                </div>
                {co.overdue > 0 && (
                  <p className="text-xs text-[#E5A09E] mt-3">⚠︎ {co.overdue} overdue</p>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* Recent activity */}
      <section>
        <h2 className="font-heading text-2xl mb-4">Recent Enterprise Activity</h2>
        <div className="regal-card overflow-hidden">
          <div className="divide-y divide-seam max-h-96 overflow-y-auto">
            {data.recent_activity.length === 0 ? (
              <div className="p-6 text-textmuted text-center text-sm">No activity yet.</div>
            ) : (
              data.recent_activity.map((a) => (
                <div key={a.id} className="px-5 py-3 hover:bg-elevated/40 flex items-center gap-3">
                  <Circle className="w-1.5 h-1.5 fill-champagne text-champagne shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-textmain truncate">
                      <span className="text-champagne font-mono text-xs">{a.action}</span>
                      {" · "}{a.target_type}
                    </p>
                    <p className="text-[11px] text-textmuted">{a.created_at.slice(0, 19).replace("T", " ")}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CommandCenter;
